import { useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ktn_admin_token", data.token);
      onLogin();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-ivory p-10 md:p-12"
      >
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 bg-blood text-goldlight flex items-center justify-center">
            <Flame size={19} strokeWidth={1.75} />
          </span>
          <div>
            <h1 className="font-serif text-2xl font-semibold text-charcoal leading-none">TheyyamNow CMS</h1>
            <p className="text-xs uppercase tracking-[0.25em] text-charcoal/50 mt-1">Admin Sign In</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-charcoal/50 font-medium">
              Email
            </label>
            <Input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@kannurtheyyamnow.com"
              className="mt-2 rounded-none border-charcoal/20 bg-white focus-visible:ring-vermillion"
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-charcoal/50 font-medium">
              Password
            </label>
            <Input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 rounded-none border-charcoal/20 bg-white focus-visible:ring-vermillion"
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-vermillion" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <motion.button
            type="submit"
            whileTap={{ scale: 0.97 }}
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-blood text-ivory text-xs uppercase tracking-[0.22em] font-medium py-4 transition-colors duration-300 hover:bg-crimson disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}
